import React, { useState } from "react";
import { Brush, Scissors, Sparkles, Camera, Star, Palette } from "lucide-react";
import { useLang } from "../../contexts/LanguageContext";
import { tr } from "../../i18n/translations";

// ─── Content ────────────────────────────────────────────────
const COLLABS = [
  {
    key: "maquillaje",
    icon: Brush,
    label: { es: "Maquillaje", en: "Makeup", jp: "メイク" },
    desc: {
      es: "Equipos de maquillaje profesional, asistentes editoriales y artistas invitadas para colaboraciones cruzadas.",
      en: "Professional makeup teams, editorial assistants and guest artists for cross-collaborations.",
      jp: "プロメイクチーム、エディトリアルアシスタント、ゲストアーティスト。",
    },
    img: "https://images.pexels.com/photos/33976561/pexels-photo-33976561.jpeg",
    partners: [
      "Maquillistas invitadas",
      "Asistentes editoriales",
      "Cross-promotion de portafolio",
      "Masterclasses conjuntas",
      "Equipos rotativos para eventos",
    ],
  },
  {
    key: "peinado",
    icon: Scissors,
    label: { es: "Peinado", en: "Hair", jp: "ヘア" },
    desc: {
      es: "Hair stylists especializadas en novias, editorial y eventos para crear el look completo.",
      en: "Hair stylists specialised in bridal, editorial and events to deliver the full look.",
      jp: "ブライダル、エディトリアル、イベントの完成形を作るヘアスタイリスト。",
    },
    img: "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?crop=entropy&cs=srgb&fm=jpg&q=85",
    partners: [
      "Hair stylists de novia",
      "Colorimetría profesional",
      "Coordinación look día D",
      "Equipos para editorial",
      "Peluquería full service",
    ],
  },
  {
    key: "eventos",
    icon: Sparkles,
    label: { es: "Eventos", en: "Events", jp: "イベント" },
    desc: {
      es: "Wedding planners, organizadores de eventos sociales, salones boutique y haciendas seleccionadas en el Bajío.",
      en: "Wedding planners, social event producers, boutique venues and curated haciendas across Bajío.",
      jp: "ウェディングプランナー、ソーシャルイベント企画、厳選ハシエンダ。",
    },
    img: "https://images.unsplash.com/photo-1519741497674-611481863552?crop=entropy&cs=srgb&fm=jpg&q=85",
    partners: [
      "Wedding planners aliadas",
      "Haciendas del Bajío",
      "Salones boutique",
      "Timing y logística",
      "Coordinación multi-vendor",
    ],
  },
  {
    key: "pasarelas",
    icon: Star,
    label: { es: "Pasarelas", en: "Runway", jp: "ランウェイ" },
    desc: {
      es: "Diseñadores, casas de moda y productoras de desfiles. Maquillaje cohesivo para todo el cast.",
      en: "Designers, fashion houses and runway producers. Cohesive makeup across the full cast.",
      jp: "デザイナー、ファッションハウス、ランウェイ制作。",
    },
    img: "https://images.unsplash.com/photo-1502823403499-6ccfcf4fb453?crop=entropy&cs=srgb&fm=jpg&q=85",
    partners: [
      "Casas de moda mexicanas",
      "Directoras creativas",
      "Producción de desfiles",
      "Cast completo cohesivo",
      "Backstage coordination",
    ],
  },
  {
    key: "influencers",
    icon: Camera,
    label: { es: "Influencers", en: "Influencers", jp: "インフルエンサー" },
    desc: {
      es: "Creadoras de contenido y embajadoras de marca. Maquillaje listo para foto y video, optimizado para redes.",
      en: "Content creators and brand ambassadors. Photo- and video-ready makeup, optimised for socials.",
      jp: "クリエイター、ブランドアンバサダー。フォト・ビデオ最適化メイク。",
    },
    img: "https://images.pexels.com/photos/8221202/pexels-photo-8221202.jpeg",
    partners: [
      "Sesiones de contenido",
      "Marca personal",
      "Optimización para redes",
      "Video-ready looks",
      "Colaboración con post-producción",
    ],
  },
  {
    key: "artistas",
    icon: Palette,
    label: { es: "Artistas", en: "Artists", jp: "アーティスト" },
    desc: {
      es: "Fotógrafas, directoras creativas, estilistas y artistas plásticas. Colaboraciones de portafolio y proyectos personales.",
      en: "Photographers, creative directors, stylists and visual artists. Portfolio collaborations and personal projects.",
      jp: "フォトグラファー、クリエイティブディレクター、スタイリスト。",
    },
    img: "https://images.unsplash.com/photo-1557924760-b14a6d4a4ef3?crop=entropy&cs=srgb&fm=jpg&q=85",
    partners: [
      "Fotógrafas editoriales",
      "Direcciones creativas",
      "Estilistas de moda",
      "Artistas plásticas",
      "Proyectos personales",
    ],
  },
];

const SECTION = {
  overline: { es: "07 — Colaboraciones", en: "07 — Collaborations", jp: "07 — コラボレーション" },
  title: { es: "Construimos en equipo.", en: "We build as a team.", jp: "チームで創ります。" },
  intro: {
    es: "Trabajo de la mano con un círculo de profesionales que comparten mi estándar de detalle. Si tu marca, evento o proyecto necesita coordinar varias disciplinas, te conecto con quien lo hace mejor.",
    en: "I work hand-in-hand with a circle of professionals who share my standard of detail. If your brand, event or project needs to coordinate disciplines, I connect you with whoever does it best.",
    jp: "細部へのこだわりを共有するプロフェッショナルのサークルと協働しています。",
  },
};

export default function Collaborations() {
  const { lang } = useLang();
  const [hovered, setHovered] = useState(null);

  return (
    <section
      id="collaborations"
      className="bg-[#FAFAFA] py-24 sm:py-32 border-t border-[#E8E1DA]"
      data-testid="collaborations-section"
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
        {/* ── Header ── */}
        <div className="grid lg:grid-cols-12 gap-10 mb-16 sm:mb-20">
          <div className="lg:col-span-6">
            <p className="overline text-[#8B6D5E] mb-4">{tr(SECTION.overline, lang)}</p>
            <h2 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[1]">
              {tr(SECTION.title, lang)}
            </h2>
          </div>
          <p className="lg:col-span-5 lg:col-start-8 text-base text-[#5C5C5C] leading-relaxed self-end">
            {tr(SECTION.intro, lang)}
          </p>
        </div>

        {/* ── Rows (OddlyMade sticky-stack pattern) ── */}
        <div className="border-t border-[#1A1A1A]/15" data-testid="collabs-list">
          {COLLABS.map((c, i) => {
            const Icon = c.icon;
            const isHovered = hovered === i;
            return (
              <div
                key={c.key}
                data-testid={`collab-row-${c.key}`}
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
                className="group sticky top-20 sm:top-24 bg-[#FAFAFA] border-b border-[#1A1A1A]/15 transition-colors"
                style={{ zIndex: 10 + i }}
              >
                <div className="grid grid-cols-12 gap-4 sm:gap-6 lg:gap-8 items-start py-8 sm:py-10 lg:py-14">
                  {/* Image */}
                  <div className="col-span-4 sm:col-span-3 lg:col-span-2">
                    <div
                      className={`relative aspect-[4/5] overflow-hidden bg-[#F4F0EC] transition-all duration-700 ${
                        isHovered ? "scale-[1.02]" : "scale-100"
                      }`}
                    >
                      <img
                        src={c.img}
                        alt={tr(c.label, lang)}
                        loading="lazy"
                        className={`absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out ${
                          isHovered ? "scale-[1.10]" : "scale-100"
                        }`}
                      />
                    </div>
                  </div>

                  {/* Number + icon + title + desc */}
                  <div className="col-span-8 sm:col-span-9 lg:col-span-6 flex flex-col justify-between min-h-[100px] sm:min-h-[140px] lg:min-h-[180px]">
                    <div className="flex items-baseline gap-3 sm:gap-6">
                      <span
                        className="overline text-[#8B6D5E] tabular-nums flex-none flex items-baseline gap-2"
                        data-testid={`collab-num-${c.key}`}
                      >
                        {String(i + 1).padStart(2, "0")}
                        <Icon size={12} className="translate-y-[1px] text-[#8B6D5E]" aria-hidden />
                      </span>
                      <h3
                        className={`font-serif text-3xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-[#1A1A1A] transition-transform duration-500 ${
                          isHovered ? "translate-x-2" : "translate-x-0"
                        }`}
                        data-testid={`collab-title-${c.key}`}
                      >
                        {tr(c.label, lang)}
                        <span className="text-[#8B6D5E]">.</span>
                      </h3>
                    </div>
                    <p className="mt-4 text-[#5C5C5C] leading-relaxed text-sm sm:text-base max-w-md hidden sm:block">
                      {tr(c.desc, lang)}
                    </p>
                  </div>

                  {/* Partners list */}
                  <div
                    className="col-span-12 lg:col-span-4 lg:pl-6 lg:border-l lg:border-[#1A1A1A]/15"
                    data-testid={`collab-partners-${c.key}`}
                  >
                    <ul className="divide-y divide-[#1A1A1A]/10">
                      {c.partners.slice(0, 5).map((p, k) => (
                        <li
                          key={k}
                          className="flex items-baseline gap-3 py-2.5 text-[#1A1A1A] text-sm sm:text-[15px]"
                        >
                          <span className="overline text-[#8B6D5E] text-[10px] flex-none w-6">
                            {String(k + 1).padStart(2, "0")}
                          </span>
                          <span className="leading-snug">{p}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
